/**
 * NdaFiles.jsx - the signed NDA on an Account / Site (T-0011, Paul 10/30/26).
 *
 * ponytail: the file rides on an activity entry (type "NDA sent / received",
 * subject "Signed NDA"), so it reuses the activity files' storage, SharePoint
 * backup and audit, and the upload also shows on the timeline. A site that
 * inherits its company's NDA shows no file here; the company's card has it.
 */
import { useEffect, useRef, useState } from 'react';
import ActivityFiles, { ACTIVITY_FILES_BASE } from './ActivityFiles';
import { pickField } from '../../data/crmStages';

export const NDA_SUBJECT = 'Signed NDA';
const NDA_TYPE = 'NDA sent / received';
const enc = encodeURIComponent;

/** This record's own "Signed NDA" entries (a company's list also carries its sites'). */
export const ndaEntries = (account) =>
  (pickField(account, 'recentActivity', 'recent_activity') || []).filter(
    (e) => e && e.subject === NDA_SUBJECT && pickField(e, 'accountId', 'account_id') === account.id
  );

export default function NdaFiles({ account, editable }) {
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const input = useRef(null);
  const ids = ndaEntries(account)
    .map((e) => e.id)
    .join(',');

  useEffect(() => {
    let live = true;
    Promise.all(
      (ids ? ids.split(',') : []).map((id) =>
        fetch(`${ACTIVITY_FILES_BASE}/activity/${enc(id)}/attachments`)
          .then((r) => (r.ok ? r.json() : []))
          .catch(() => [])
      )
    ).then((lists) => live && setFiles(lists.flat()));
    return () => {
      live = false;
    };
  }, [ids]);

  const attach = async (file) => {
    if (!file) return;
    setBusy(true);
    setErr('');
    try {
      const r = await fetch(`/api/accounts/${enc(account.id)}/activity`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          date: new Date().toLocaleDateString('en-CA'), // local YYYY-MM-DD
          type: NDA_TYPE,
          subject: NDA_SUBJECT,
          text: `Signed NDA attached: ${file.name}`,
        }),
      });
      const entry = r.ok ? await r.json() : null;
      if (!entry || !entry.id) throw new Error('entry');
      const fd = new FormData();
      fd.append('file', file);
      fd.append('doc_type', 'general');
      const up = await fetch(`${ACTIVITY_FILES_BASE}/activity/${enc(entry.id)}/attachments`, { method: 'POST', body: fd });
      if (!up.ok) throw new Error('upload');
      const saved = await up.json().catch(() => ({ id: entry.id, filename: file.name, size: file.size }));
      setFiles((f) => [saved, ...f]);
    } catch (e) {
      setErr("The NDA didn't attach - try again (PDF, Word or an image).");
    } finally {
      setBusy(false);
      if (input.current) input.current.value = '';
    }
  };

  if (!files.length && !editable) return null;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, width: '100%', paddingTop: 4 }}>
      <ActivityFiles files={files} />
      {editable && (
        <span>
          <input
            ref={input}
            type="file"
            accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
            style={{ display: 'none' }}
            aria-label="Signed NDA file"
            onChange={(e) => attach(e.target.files && e.target.files[0])}
          />
          <button
            type="button"
            disabled={busy}
            onClick={() => input.current && input.current.click()}
            style={{
              fontSize: 10.5,
              fontWeight: 800,
              fontFamily: 'Montserrat',
              padding: '3px 8px',
              borderRadius: 6,
              border: '1px solid #CFD8DC',
              background: '#fff',
              cursor: busy ? 'wait' : 'pointer',
            }}
          >
            {busy ? 'Attaching…' : files.length ? 'Attach another signed NDA' : 'Attach signed NDA'}
          </button>
        </span>
      )}
      {err && <span style={{ fontSize: 10.5, color: '#B71C1C' }}>{err}</span>}
    </div>
  );
}
